import Player from 'video.js/dist/types/player'

import { MAX_PLAYBACK_RATE, WATCH_CREDIT_SLACK_SECONDS } from './VideoPlayer.const'
import { Cues } from './VideoPlayer.types'

const findAllElement = (element: HTMLElement | null, i: string): NodeListOf<HTMLElement> | null => {
  if (!element) return null
  try {
    return element.querySelectorAll(i)
  } catch (e) {
    return null
  }
}

const showChapterTooltips = (e: any, label: string, left: number): void => {
  const tooltipEle = document.querySelector('.vjs-chapter') as HTMLElement
  if (tooltipEle) {
    tooltipEle.innerHTML = label
    tooltipEle.style.opacity = '1'
    tooltipEle.style.visibility = 'visible'
    tooltipEle.style.left = `${left}%`
  }
}

const togglePlay = (player: Player): void => {
  if (player.paused()) {
    player.play()
  } else {
    player.pause()
  }
}

const removeChapterTooltips = (): void => {
  const tooltipEle = document.querySelector('.vjs-chapter') as HTMLElement
  if (tooltipEle) {
    tooltipEle.style.opacity = '0'
    tooltipEle.style.visibility = 'hidden'
  }
}

const getPlayerValues = (player: Player): { currentTime: number; duration: number; volume: number } => {
  const currentTime = player.currentTime() || 0
  const duration = player.duration() || 1
  const volume = player.volume() || 0
  return { currentTime, duration, volume }
}

export const addChapterMarkers = (player: Player, videoElement: HTMLVideoElement): void => {
  // add chapter markers
  const tracks = player.textTracks().tracks_
  const chapter = tracks.find((track: { kind: string }) => track.kind === 'chapters')
  const progressControl = findAllElement(videoElement?.parentElement, '.vjs-progress-holder')
  if (!chapter || !progressControl?.length) return

  const g = document.createElement('div')
  g.className = 'vjs-chapter'
  progressControl[0].appendChild(g)

  // remove all old chapter markers
  const oldChapterMarkers = findAllElement(progressControl[0], '.vjs-marker')
  oldChapterMarkers?.forEach((marker) => marker.remove())

  chapter.cues_.forEach((cue: Cues) => {
    const chapterMarkers = document.createElement('div')
    const { duration } = getPlayerValues(player)
    const left = (cue.startTime / duration) * 100

    if (left <= 100) {
      chapterMarkers.classList.add('vjs-marker')
      chapterMarkers.innerHTML = '<div class="vjs-marker-inn"></div>'
      chapterMarkers.style.left = `${left}%`
      chapterMarkers.addEventListener(
        'mouseover',
        (e) => {
          showChapterTooltips(e, cue.text, left)
        },
        !1,
      )
      chapterMarkers.addEventListener('mouseout', removeChapterTooltips, !1)
      progressControl[0].appendChild(chapterMarkers)
    }
  })

  const getChapterLabel = (currentTime: number): string => {
    const cues = chapter.cues_.find((cue: Cues) => cue.startTime <= currentTime && currentTime <= cue.endTime)
    return cues?.text || ''
  }
  // add touch events
  progressControl[0].addEventListener(
    'touchstart',
    () => {
      document.addEventListener('touchmove', (e: TouchEvent) => {
        const { currentTime, duration } = getPlayerValues(player)
        const leftPercent = (currentTime / duration) * 100
        if (leftPercent > 0) {
          showChapterTooltips(e, getChapterLabel(currentTime), leftPercent)
        }
      })
      document.addEventListener('touchend', removeChapterTooltips)
    },
    { passive: true },
  )
}

export const addPlayerEvents = (player: Player): void => {
  player.on('fullscreenchange', (e: any) => {
    e.preventDefault()
    player.focus()
  })
  player.on('keydown', (event: KeyboardEvent) => {
    event.preventDefault()
    const { currentTime, duration, volume } = getPlayerValues(player)
    const timeDiff = 10
    const volumeDiff = 0.07

    const keyCode = event.code
    switch (keyCode) {
      case 'Space':
        togglePlay(player)
        break
      case 'ArrowRight':
        if (duration - currentTime > timeDiff) {
          player.currentTime(currentTime + timeDiff)
        } else {
          player.currentTime(duration - 1)
        }
        break
      case 'ArrowLeft':
        if (currentTime < timeDiff) {
          player.currentTime(0)
        } else {
          player.currentTime(currentTime - timeDiff)
        }
        break
      case 'ArrowUp':
        player.volume(volume + volumeDiff)
        break
      case 'ArrowDown':
        player.volume(volume - volumeDiff)
        break

      case 'KeyF':
        if (player.isFullscreen()) {
          player.exitFullscreen()
        } else {
          player.requestFullscreen()
        }
        break
    }
  })
}

/**
 * How much of a jump in playback position counts as watching.
 *
 * The watch timer samples the player's position on an interval and credits the distance covered. A
 * seek moves that position without anyone watching, and `seeked` firing after the fact is not
 * enough to catch it: dragging the scrubber takes longer than one sample, and video.js moves
 * currentTime during the drag, so a sample lands mid-drag and reads the whole jump as watched.
 *
 * Real time is the bound that actually holds. Whatever the position did, a viewer cannot have seen
 * more content than the seconds that passed allow at the fastest playback rate. That keeps a
 * backgrounded tab honest too — its timer fires late, so it is owed the whole gap — while a seek,
 * which covers minutes inside one sample, is cut down to what that sample could have shown.
 */
export const creditableWatchSeconds = (positionMoved: number, secondsPassed: number): number => {
  if (positionMoved <= 0) return 0

  const mostThatCouldBeWatched = Math.max(secondsPassed, 0) * MAX_PLAYBACK_RATE + WATCH_CREDIT_SLACK_SECONDS

  return Math.min(positionMoved, mostThatCouldBeWatched)
}
